<template>
    <Input
        :id="id"
        :invalidMessage="type === 'hidden' ? '' : errorMessage"
        :label="label"
        :leadingIcon="leadingIcon"
        :maxlength="maxlength"
        :minlength="minlength"
        :type="type"
        :rows="rows"
        :disabled="disabled"
        @blur="handleBlur"
        @focus="handleFocus"
        @input="handleInput">
        <slot/>
    </Input>
</template>

<script setup lang="ts">
import { Input } from '@helcim/blizzard';
import { withDefaults, inject, onBeforeUnmount, onMounted, ref, watch} from 'vue';
import { Emitter, EventType } from 'mitt';

type InputType =
    | 'text'
    | 'number'
    | 'date'
    | 'email'
    | 'select'
    | 'textarea'
    | 'password'
    | 'hidden'
    | 'tel'
    | 'accessible-password'

const emit = defineEmits<{
    (event: 'blur', value: FocusEvent): void,
    (event: 'focus', value: FocusEvent): void,
    (event: 'input', value: InputEvent): void,
}>();

const props = withDefaults(defineProps<{
    requiredError?: string,
    errorOnInput?: boolean,
    numbersOnly?: boolean,
    label: string,
    type?: InputType,
    id: string,
    options?: Record<string, string>,
    leadingIcon?: string,
    maxlength?: number | string,
    minlength?: number | string,
    rows?: number,
    invalidMessage?: string,
    disabled?: boolean,
}>(), {
    requiredError: '',
    errorOnInput: false,
    numbersOnly: false,
    type: 'text',
    options: null,
    leadingIcon: '',
    maxlength: '',
    minlength: '',
    rows: 1,
    invalidMessage: '',
    disabled: false,
});

const input = ref('');
const entered = ref(props.errorOnInput);
const errorMessage = ref('');
const emitter = inject<Emitter<Record<EventType, unknown>>>('emitter');

const handleErrorMessage = (invalidMessage?: string): void => {
    if (!input.value.length) {
        if (props.requiredError) {
            errorMessage.value = props.requiredError;
            return;
        }
        if (props.invalidMessage) {
            errorMessage.value = props.invalidMessage;
            return;
        }
        errorMessage.value = invalidMessage;
        return;
    }
    errorMessage.value = invalidMessage ? invalidMessage : props.invalidMessage;
};

const setInput = (target: HTMLElement | EventTarget) => {
    if(target instanceof HTMLInputElement) { input.value = target.value; }
    if(target instanceof HTMLSelectElement && target.selectedIndex !== -1) {
        input.value = target.options[target.selectedIndex].text;
    }
};

const handleBlur = (event: FocusEvent) => {
    emit('blur', event);
    entered.value = true;
    if(!props.errorOnInput) { handleErrorMessage(); }
};

const handleFocus = (event: FocusEvent) => {
    emit('focus', event);
};

const handleInput = (event: InputEvent) => {
    emit('input', event);
    if(!props.errorOnInput) { errorMessage.value = ''; }
    setInput(event.target);
};

if(props.errorOnInput) {
    watch([input, entered, () => props.invalidMessage], () => {
        handleErrorMessage();
    });
}

onMounted(() => {
    emitter.on(`${props.id}ErrorMessage`, handleErrorMessage);
    setInput(document.getElementById(props.id));
});

onBeforeUnmount(() => {
    emitter.off(`${props.id}ErrorMessage`, handleErrorMessage);

});
</script>
